// Текущий пользователь: роль, сеть, основная точка.
//
// Живёт в shared/, а не в модуле: роль нужна каждому домену, и импорт сессии из
// modules/catalog в modules/printing нарушил бы границы модулей (app.md §3).
//
// Guard работает ПО КЭШУ и без сети. Причина в инварианте 3: WiFi на кухне нестабилен,
// и guard, выкидывающий менеджера на /login при каждом обрыве, убивал бы Модуль Б
// на ровном месте. Войти офлайн всё равно невозможно (offline-first.md §4), поэтому
// протухание сессии обнаруживается первым 401 от сервера, а не локальным таймером.

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { httpClient, HttpError, isUndelivered } from '@/shared/api';
import type { components } from '@/shared/api/generated/schema';
import { clearToken, getToken, isSupportSession } from '@/shared/auth';
import { clearQuerySnapshot, forgetProfile, readProfile, rememberProfile } from '@/shared/offline';
import { confirmLegacyPushServer, forgetPushOnLogout, isLegacyPushServer } from '@/shared/push';
import { clearPersonPreferences } from '@/shared/storage/preferenceKeys';

export type CurrentUser = components['schemas']['CurrentUser'];

export const sessionKeys = {
  me: ['session', 'me'] as const,
} as const;

/** Почему сессия кончилась — ради текста на экране входа: «сбросили» и «истекла» требуют разного. */
export type SessionEndReason = 'session_revoked' | 'session_expired';

/**
 * Отказ сервера субъекту (401/403) — вместе с токеном, которым ушёл запрос.
 *
 * Токен нужен потому, что отказ живёт в кэше запросов дольше сессии, которой он адресован
 * (B-034): после повторного входа устаревший 401 прошлой сессии стирал уже новый токен.
 * Сверка «отказ пришёл текущему токену» — единственное, что отличает мёртвую сессию
 * от памяти о ней.
 *
 * Наследник `HttpError`, а не отдельный класс: всё, что уже разбирает отказ по статусу
 * (ретраи, экраны ошибок), продолжает его узнавать.
 */
export class SessionRejectedError extends HttpError {
  constructor(
    source: HttpError,
    /** Токен, с которым ушёл запрос; `null` — запрос ушёл без сессии. */
    readonly token: string | null,
  ) {
    super(source.status, source.statusText, source.payload);
    this.name = 'SessionRejectedError';
  }

  /** Причина от сервера: `session_expired` — истекла по бездействию, всё прочее — сброшена. */
  get reason(): SessionEndReason {
    const payload = this.payload as { error?: { code?: string } } | undefined;
    return payload?.error?.code === 'session_expired' ? 'session_expired' : 'session_revoked';
  }
}

/**
 * Унести с устройства всё, что принадлежит человеку, — кроме токена и очереди.
 *
 * Одна функция на выход и на вход следующего человека (B-034): выкидывание отказом
 * сервера не проходит через «Выйти», и то, что уносит выход, обязан унести вход того,
 * кто пришёл после. Два списка очистки однажды уже разъехались бы.
 */
export async function forgetPersonData(
  queryClient: QueryClient,
  userId: string | null,
): Promise<void> {
  // Диск чистится наравне с памятью. Кэш TanStack Query живёт до перезапуска,
  // а Preferences переживают его — и меню предыдущего человека всплывало бы
  // у следующего как своё, вместе с его сроками годности (риск Р5).
  await clearPersonPreferences(userId);
  // Профиль с диска уходит вместе с ключами: он несёт имя, роль и точку ушедшего.
  await forgetProfile();
  // Снимок кэша кабинета на диске — тоже данные ушедшего (каталог, персонал его
  // сети). Без этого следующий вход на том же устройстве показал бы чужой каталог
  // до первого ответа сервера (тот же риск Р5, что у Preferences).
  await clearQuerySnapshot();
  // Кэш чистится целиком: в нём лежат данные сети и точки предыдущего пользователя.
  // Очередь непросинканных операций (IndexedDB, T-007) при этом НЕ трогается —
  // иначе выход менеджера тихо уносил бы напечатанное поваром.
  queryClient.clear();
}

export function useSession() {
  return useQuery({
    queryKey: sessionKeys.me,
    queryFn: async (): Promise<CurrentUser> => {
      // Токен запоминается ДО запроса: заголовок собирается из него же, и отказ сервера
      // относится к нему, а не к тому, что окажется в памяти к моменту ответа.
      const token = getToken();
      try {
        const profile = await httpClient.get<CurrentUser>('/tenants/me/');
        // На диск — сразу: кэш TanStack Query живёт только в памяти, а из профиля
        // берутся личность автора печати и таймзона точки. Приложение, перезапущенное
        // без сети, иначе печатать не может вовсе (инвариант 3).
        await rememberProfile(profile);
        return profile;
      } catch (error) {
        // Отказ сервера субъекту (401/403) кэш НЕ подменяет: сессия мертва, и работать
        // по сохранённому профилю значило бы держать открытым кабинет уволенного.
        // Подменяется только отсутствие СЕТИ.
        if (error instanceof HttpError && (error.status === 401 || error.status === 403)) {
          throw new SessionRejectedError(error, token);
        }
        // В саппорт-сессии (T-039) на диске лежит профиль САМОЙ платформы, а не того,
        // чьими глазами она смотрит: подставить его значило бы показать чужую роль.
        if (error instanceof HttpError || isSupportSession()) {
          throw error;
        }
        const cached = await readProfile();
        if (cached) {
          return cached as CurrentUser;
        }
        throw error;
      }
    },
    // Без токена запрос не имеет смысла: он гарантированно вернёт 401 и только
    // засорит консоль на экране логина.
    enabled: Boolean(getToken()),
    // Профиль МЕНЯЕТСЯ не только при входе — этой же учётке точку могут назначить
    // (или сменить) с ДРУГОГО устройства/сессии, пока эта открыта (пилот, 2026-09,
    // Тихонов Р.: назначили точку админу — на его экране печати «не назначена точка»
    // держалось около часа). Канала «сервер попросил синкнуться» нет и не будет
    // (устройство за NAT, `useSync.ts`), поэтому единственный практичный фикс —
    // конечный staleTime + периодический рефетч, а не бесконечное доверие профилю,
    // полученному при входе. `refetchInterval` крутится, только пока есть смонтированный
    // наблюдатель (открытый экран) — простаивающему в фоне приложению лишних
    // запросов это не добавляет.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    // Не ретраим: 401 означает решение сервера, повторять его бессмысленно.
    // NetworkError при этом ретраится общей политикой queryClient — данные придут
    // из кэша, и guard продолжит работать офлайн.
    retry: (failureCount, error) => !(error instanceof HttpError) && failureCount < 2,
  });
}

const LOGOUT_PATH = '/auth/logout/';

/**
 * Потолок ожидания сервера при выходе (B-039).
 *
 * У fetch таймаута нет, и на зависшей сети «Выйти» висело до обрыва соединения —
 * на пересменке, где кнопку нажимают, чтобы отдать телефон следующему. Сам выход
 * локальный и от ответа не зависит; ответ нужен серверу — погасить сессию.
 */
const LOGOUT_TIMEOUT_MS = 4_000;

/** Как часто досылать недошедший выход, пока приложение открыто (как фоновая проверка очереди). */
const DEFERRED_LOGOUT_RETRY_MS = 30_000;

/**
 * Выходы, не дошедшие до сервера (B-039).
 *
 * Пока сессия жива на сервере, push её владельца идёт на этот телефон — даже когда
 * на нём уже работает следующий. Поэтому недошедший выход досылается, как только
 * появится сеть. Токен — ТОЛЬКО в памяти: на диск секрет ушедшего не возвращается.
 * Перезапустили приложение — сессию снимет следующий вход (`sync` освободит адрес
 * установки) или idle-TTL сервера.
 */
const undeliveredLogouts = new Set<string>();
let stopLogoutRetries: (() => void) | null = null;
let retryingLogouts = false;

function deferLogout(token: string): void {
  undeliveredLogouts.add(token);
  if (stopLogoutRetries !== null) return;

  const retry = (): void => {
    void retryDeferredLogouts();
  };
  // Кроме события `online` — таймер: при зависшей сети `navigator.onLine` всё время
  // врёт «онлайн», и события не бывает вовсе (`useSync.ts`).
  const tick = (): void => {
    if (navigator.onLine) retry();
  };
  window.addEventListener('online', retry);
  const timer = setInterval(tick, DEFERRED_LOGOUT_RETRY_MS);
  stopLogoutRetries = () => {
    window.removeEventListener('online', retry);
    clearInterval(timer);
    stopLogoutRetries = null;
  };
}

async function retryDeferredLogouts(): Promise<void> {
  if (retryingLogouts) return;
  retryingLogouts = true;
  try {
    for (const token of [...undeliveredLogouts]) {
      try {
        // Токен ушедшего — ЯВНЫМ заголовком: если здесь уже вошёл следующий, текущая
        // сессия — его, и выход под ней закрыл бы работающего человека.
        await withinLogoutTimeout((signal) =>
          httpClient.post<void>(LOGOUT_PATH, undefined, {
            headers: { Authorization: `Bearer ${token}` },
            signal,
          }),
        );
        undeliveredLogouts.delete(token);
      } catch (error) {
        // Сервер ответил отказом (401 — сессии уже нет): повторять нечего.
        if (!isUndelivered(error)) undeliveredLogouts.delete(token);
      }
    }
  } finally {
    retryingLogouts = false;
    if (undeliveredLogouts.size === 0) stopLogoutRetries?.();
  }
}

/**
 * Запросы выхода — под одним потолком ожидания. Отмена по таймауту приходит
 * `NetworkError`, как обрыв: для выхода это одно и то же — «не дошло».
 */
async function withinLogoutTimeout(send: (signal: AbortSignal) => Promise<void>): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS);
  try {
    await send(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

/** Завершение сессии: чистит токен и весь кэш server state. */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = getToken();
      // Саппорт-сессия push не трогает (B-039): сессия чужая, а адрес на телефоне —
      // самого платформенного админа.
      const support = isSupportSession();
      try {
        await withinLogoutTimeout(async (signal) => {
          // Push при выходе больше не снимается (B-039): выбор хранит сервер у человека,
          // адрес принадлежит установке, а доставку на телефон сервер прекращает сам,
          // гася сессию на `/auth/logout/`. Раньше здесь удалялась строка на сервере —
          // она и была выбором, и после «вышел → вошёл» push не приходил.
          //
          // Прежний сервер (404 на alert-settings) — как раньше: у него строка телефона
          // и есть выбор, и не снять её значит оставить push ушедшего следующему.
          // Флаг только повод спросить: сервер могли обновить, пока приложение открыто,
          // а `forget/` на новом сервере выключил бы push человеку на всех телефонах —
          // тот же B-039. Поэтому `forget/` — только после свежего 404.
          // Потолок общий с выходом: всё вместе не ждёт дольше LOGOUT_TIMEOUT_MS.
          if (!support && isLegacyPushServer() && (await confirmLegacyPushServer(signal))) {
            await forgetPushOnLogout({ signal });
          }
          // Напрямую через httpClient, а не через `authApi` модуля входа: нужен сигнал
          // таймаута, а `shared/` не зависит от `modules/` (app.md §3).
          await httpClient.post<void>(LOGOUT_PATH, undefined, { signal });
        });
      } catch (error) {
        // Сервер мог не ответить (обрыв, зависшая сеть, севшая сессия). Локальный выход
        // всё равно обязан состояться: иначе кнопка «Выйти» на планшете в общем цеху
        // не работает без сети, а именно там она и нужна. Недошедший — дошлётся с сетью.
        if (token !== null && isUndelivered(error)) deferLogout(token);
      }
    },
    onSettled: async () => {
      // Кто уходит — читается ДО очистки кэша: после `queryClient.clear()` профиля
      // уже нет, и скоуп пришлось бы чистить вслепую за всех.
      const leaving = queryClient.getQueryData<CurrentUser>(sessionKeys.me)?.id ?? null;

      await clearToken();
      await forgetPersonData(queryClient, leaving);
    },
  });
}
